import { useEffect, useState } from 'react'
import type { OverflowWindowInfo } from '@/types/api'
import { getTimeRemaining } from '@/utils/timer'
import { OverflowQualityWarning } from './OverflowQualityWarning'

interface OverflowBannerProps {
  overflowWindow: OverflowWindowInfo | null
  overflowIsLocked: boolean
  priorityRank: number | null
  priorityTotal: number
  hasOverflowCapacity: boolean
  offerSlug?: string
}

function fmtCountdown(targetIso: string): string {
  const rem = getTimeRemaining(new Date(targetIso))
  if (rem.hours > 0) return `${rem.hours}h ${rem.minutes}m`
  return `${rem.minutes}m ${rem.seconds}s`
}

export function OverflowBanner({
  overflowWindow,
  overflowIsLocked,
  priorityRank,
  priorityTotal,
  hasOverflowCapacity,
  offerSlug,
}: OverflowBannerProps) {
  const [, tick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [])

  if (!overflowWindow && !hasOverflowCapacity) return null

  const locked = overflowIsLocked || overflowWindow?.overflow_locked
  const inPriorityWindow = overflowWindow?.is_priority_window
  const publicNote =
    offerSlug === 'testerup'
      ? 'Public overflow geos: US, UK.'
      : offerSlug === 'freecash'
        ? 'Public overflow geos: FR, AT, IT, PL, BE, ES.'
        : offerSlug === 'playful'
          ? 'Public overflow geos: DE, FR, IT, ES, SE.'
          : offerSlug === 'teststar'
            ? 'Public overflow: whole offer ($5K).'
            : null

  return (
    <div
      className={
        'mb-5 rounded-xl border px-4 py-3 ' +
        (locked
          ? 'border-amber-300/60 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10'
          : 'border-brand-blue/30 bg-brand-blue/5')
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-text-primary">
            {locked ? 'Overflow pool locked' : 'Overflow pool open'}
          </p>
          <p className="text-xs text-text-muted mt-0.5 max-w-xl">
            {inPriorityWindow
              ? 'Priority window (00:00–01:00 UTC): top-ranked affiliates get first claim. Pool opens to all after.'
              : locked
                ? 'Overflow requests are paused until the pool unlocks.'
                : `Request overflow on approved geos${publicNote ? `, or public geos (${publicNote.replace('Public overflow geos: ', '')})` : ''}.`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-xs">
          {priorityRank != null && priorityTotal > 0 && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-text-muted">Your priority</p>
              <p className="font-semibold text-text-primary tabular-nums">
                #{priorityRank} <span className="text-text-muted font-normal">of {priorityTotal}</span>
              </p>
            </div>
          )}
          {!locked && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-text-muted">Status</p>
              <p className="font-semibold text-text-primary">
                {hasOverflowCapacity ? 'Capacity available' : 'Depleted'}
              </p>
            </div>
          )}
          {locked && overflowWindow?.opens_at_utc && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-text-muted">Opens in</p>
              <p className="font-semibold text-text-primary tabular-nums">
                {fmtCountdown(overflowWindow.opens_at_utc)}
              </p>
            </div>
          )}
        </div>
      </div>
      {!locked && <OverflowQualityWarning className="mt-3" />}
    </div>
  )
}
